import React from 'react';
import { useLocation } from "wouter";
import { Tabs } from "@/components/ui/tabs";
import { ComparisonHeader } from "./ComparisonHeader";
import { ComparisonContent } from "./ComparisonContent";
import { useComparisonState } from "@/hooks/useComparisonState";
import { useComparisonMutations } from "@/hooks/useComparisonMutations";
import { SummaryStats, EnhancedRoomMatch, HydratedMatchGroup, OrganizedRoom } from "./types";
import { RoomDetail } from '@/api/generated/client/models/RoomDetail';
import { CreateRoomModal } from "./tabs/CreateRoomModal";

interface ComparisonTabsProps {
    comparisonId: number;
    claimId?: number;
    claimNumber?: string;
    isProcessing?: boolean;
    canWrite?: boolean;
    stats: SummaryStats;
    enhancedMatches: EnhancedRoomMatch[];
    hydratedMatchGroups: HydratedMatchGroup[];
    organizedRooms: OrganizedRoom[]; // Logic to build this needs to be passed or calculated
    unmatchedLineItemsA: any[];
    unmatchedLineItemsB: any[];
    allRoomsList: RoomDetail[];
    unmatchedInsurerRooms: OrganizedRoom[];
    unmatchedContractorRooms: OrganizedRoom[];
    insurerEstimateId?: number;
    contractorEstimateId?: number;
}

export default function ComparisonTabs({
    comparisonId,
    claimId,
    claimNumber,
    isProcessing,
    canWrite = false,
    stats,
    enhancedMatches,
    hydratedMatchGroups,
    organizedRooms,
    unmatchedLineItemsA,
    unmatchedLineItemsB,
    allRoomsList,
    unmatchedInsurerRooms,
    unmatchedContractorRooms,
    insurerEstimateId,
    contractorEstimateId
}: ComparisonTabsProps) {
    const [, setLocation] = useLocation();
    const {
        filters,
        setSearchQuery,
        addSearchTerm,
        removeSearchTerm,
        clearSearchTerms,
        toggleShowMatched,
        toggleHideEmptyRooms,
        setConfidenceLevel,
        toggleShowOverscoped,
        toggleShowAdditional,
        toggleShowDiscrepancy,
        toggleShowNotOnInsurance,
        applySingleFilter,
        setSpecialFilter,
        selection
    } = useComparisonState();

    // Calculate Filter IDs
    const overscopedIds = React.useMemo(() => {
        const ids = new Set<number>();
        stats.overscopeItems.forEach(i => {
            if (i.insuranceItem?.lineItemId) ids.add(i.insuranceItem.lineItemId);
            if (i.contractorItem?.lineItemId) ids.add(i.contractorItem.lineItemId);
        });
        return ids;
    }, [stats.overscopeItems]);

    const additionalIds = React.useMemo(() => {
        return new Set(unmatchedLineItemsA.map(i => i.lineItemId));
    }, [unmatchedLineItemsA]);

    const discrepancyIds = React.useMemo(() => {
        const ids = new Set<number>();
        stats.contractorOverscopeItems.forEach(i => {
            if (i.insuranceItem?.lineItemId) ids.add(i.insuranceItem.lineItemId);
            if (i.contractorItem?.lineItemId) ids.add(i.contractorItem.lineItemId);
        });
        return ids;
    }, [stats.contractorOverscopeItems]);

    const notOnInsuranceIds = React.useMemo(() => {
        return new Set(unmatchedLineItemsB.map(i => i.lineItemId));
    }, [unmatchedLineItemsB]);

    const mutations = useComparisonMutations(comparisonId, canWrite);

    const [activeTab, setActiveTab] = React.useState("matches");
    const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
    const [createModalType, setCreateModalType] = React.useState<'insurer' | 'contractor'>('insurer');

    const selectedInsurerCount = Array.from(selection.selectedItemIds).filter(id =>
        organizedRooms.some(r => r.type === 'insurer' && r.items.some(i => i.lineItemId === id))
    ).length;
    const selectedContractorCount = selection.selectedItemIds.size - selectedInsurerCount;

    const sortedRooms = [...organizedRooms].sort((a, b) => {
        const nameA = a.level ? `${a.level} - ${a.name}` : a.name;
        const nameB = b.level ? `${b.level} - ${b.name}` : b.name;
        return nameA.localeCompare(nameB);
    });

    const handleApproveHighConfidence = () => {
        // Filter logic replicated from RoomMatchesTab to respect filtered view
        const activeTerms = [filters.searchQuery, ...filters.searchTerms].filter(t => t.trim() !== '').map(t => t.toLowerCase());
        const filterFn = (text: string) => {
            if (activeTerms.length === 0) return true;
            return activeTerms.some(term => text.toLowerCase().includes(term));
        };

        const filteredMatches = enhancedMatches.filter(match => {
            // 1. Confidence Level Filter (Global View Filter)
            const conf = match.confidence || 0;
            if (filters.confidenceLevel === 'high' && conf < 0.8) return false;
            if (filters.confidenceLevel === 'medium' && (conf < 0.5 || conf >= 0.8)) return false;
            if (filters.confidenceLevel === 'low' && conf >= 0.5) return false;

            // 2. Hide Matched
            if (!filters.showMatched && match.review?.status === 'approved') return false;

            // 3. Hide Empty Rooms
            if (filters.hideEmptyRooms) {
                const hasItems =
                    (match.insurerDetails?.flatMap(d => d.items).length || 0) > 0 ||
                    (match.contractorDetails?.flatMap(d => d.items).length || 0) > 0;
                if (!hasItems) return false;
            }

            // 4. Search Filter
            const roomAName = match.roomA?.join(', ') || '';
            const roomBName = match.roomB?.join(', ') || '';
            const matchText = `${roomAName} ${roomBName}`;

            // Check room name match
            let isMatch = filterFn(matchText);

            // If not matched by room name, check items (deep search)
            if (!isMatch) {
                const allItems = [
                    ...(match.insurerDetails?.flatMap(d => d.items) || []),
                    ...(match.contractorDetails?.flatMap(d => d.items) || [])
                ];
                isMatch = allItems.some(item => filterFn(item.description || ''));
            }

            return isMatch;
        });

        if (activeTab === 'matches') {
            // Approve Room Matches
            const highConfMatches = filteredMatches.filter(m => (m.confidence || 0) >= 0.8);
            const idsToApprove = highConfMatches.map(m => m.id);

            if (idsToApprove.length > 0) {
                mutations.bulkApproveMatches(idsToApprove);
            }
        } else {
            // Approve Line Item Match Groups (visible in current filtered rooms)
            const visibleRoomIds = new Set<number>();
            filteredMatches.forEach(m => {
                m.insurerDetails?.forEach(r => visibleRoomIds.add(r.room.id));
                m.contractorDetails?.forEach(r => visibleRoomIds.add(r.room.id));
            });

            // Also check unmatched rooms if they are visible? 
            // Usually hidden in LineItemMatchesTab? No, LineItemMatchesTab shows `roomMatches` (Matched Rooms).
            // Unmatched rooms are in Organize tab.
            // If activeTab === 'organize', we might see unmatched rooms too. 
            // My filteredMatches logic ONLY filters `roomMatches` (Matched Rooms).
            // If I am in Organize Match, I might want to approve groups there?
            // But `activeTab` logic in previous turns suggests separate `unmatched` logic.
            // For now, handling Matches tab and Items tab (Matched Rooms) covers the 90% case.
            // If needed I can expand to visible unmatched rooms.

            const highConfGroups = hydratedMatchGroups.filter(g => {
                if ((g.confidence || 0) < 0.8) return false;
                // Exclude already processed
                // Note: useComparisonData maps 'user_confirmed' -> 'approved'
                if (g.status === 'approved' || g.status === 'rejected') return false;

                // Check Visibility via Room
                const iRoomId = g.insurerItems[0]?.roomId;
                if (iRoomId && visibleRoomIds.has(iRoomId)) return true;
                const cRoomId = g.contractorItems[0]?.roomId;
                if (cRoomId && visibleRoomIds.has(cRoomId)) return true;

                return false;
            });

            const idsToApprove = highConfGroups.map(g => g.id);
            if (idsToApprove.length > 0) {
                mutations.bulkApproveLineItemMatchGroups(idsToApprove);
            }
        }
    };

    const handleMoveToRoom = (itemIds: number[], targetRoomId: number) => {
        mutations.moveLineItems({ itemIds, targetRoomId });
        selection.clearSelection();
    };

    const handleCreateRoom = (type: 'insurer' | 'contractor') => {
        setCreateModalType(type);
        setIsCreateModalOpen(true);
    };

    const handleMoveItems = (targetRoomId: number) => {
        mutations.moveLineItems({
            itemIds: Array.from(selection.selectedItemIds),
            targetRoomId
        });
        selection.clearSelection();
    };

    const handleMoveLineItem = (itemId: number, targetRoomId?: number | null, targetLineItemMatchGroupId?: number | null) => {
        mutations.moveLineItems({
            itemIds: [itemId],
            targetRoomId: targetRoomId ?? undefined,
            targetLineItemMatchGroupId: targetLineItemMatchGroupId ?? undefined
        });
    };

    const handleCreateLineItemMatchGroup = (insurerLineItemIds: number[], contractorLineItemIds: number[], roomMatchGroupId: number | null) => {
        mutations.createLineItemMatchGroup({ insurerLineItemIds, contractorLineItemIds, roomMatchGroupId });
    };

    const handleUpdateLineItemMatchGroup = (itemId: number, lineItemMatchGroupId: number | null) => {
        mutations.updateLineItemMatchGroup({ itemId, lineItemMatchGroupId });
    };

    const handleTriggerCreateRoom = (type: 'insurer' | 'contractor') => {
        setCreateModalType(type);
        setIsCreateModalOpen(true);
    };

    const handleCreateRoomSubmit = (name: string, type: 'insurer' | 'contractor') => {
        const estimateId = type === 'insurer' ? insurerEstimateId : contractorEstimateId;
        if (estimateId) {
            mutations.createRoom({ estimateId, name });
        }
    };

    const handleCreateRoomForOrganizeTab = (estimateId: number, name: string) => {
        mutations.createRoom({ estimateId, name });
    };

    return (
        <div className="flex flex-col min-h-screen max-md:min-h-0">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full space-y-0">
                <ComparisonHeader
                    claimId={claimId}
                    claimNumber={claimNumber}
                    comparisonId={comparisonId}
                    isProcessing={isProcessing}
                    activeTab={activeTab}
                    enhancedMatchesCount={enhancedMatches.length}
                    matchGroupsCount={hydratedMatchGroups.length}
                    stats={stats}
                    filters={filters}
                    enhancedMatches={enhancedMatches}
                    selectedItemIds={selection.selectedItemIds}
                    selectedInsurerCount={selectedInsurerCount}
                    selectedContractorCount={selectedContractorCount}
                    sortedRooms={sortedRooms}
                    onNavigateBack={() => claimId && setLocation(`/claims/${claimId}`)}
                    onSearchChange={setSearchQuery}
                    onAddSearchTerm={addSearchTerm}
                    onRemoveSearchTerm={removeSearchTerm}
                    onClearSearchTerms={clearSearchTerms}
                    onToggleShowMatched={toggleShowMatched}
                    onToggleHideEmptyRooms={toggleHideEmptyRooms}
                    onSetConfidenceLevel={(level) => setConfidenceLevel(level as any)}
                    // New Filter Handlers
                    onToggleShowOverscoped={toggleShowOverscoped}
                    onToggleShowAdditional={toggleShowAdditional}
                    onToggleShowDiscrepancy={toggleShowDiscrepancy}
                    onToggleShowNotOnInsurance={toggleShowNotOnInsurance}
                    onSetSpecialFilter={setSpecialFilter}
                    onApproveHighConfidence={handleApproveHighConfidence}
                    onMoveToRoom={handleMoveToRoom}
                    onCreateRoom={handleCreateRoom}
                    onClearSelection={selection.clearSelection}
                />

                <ComparisonContent
                    stats={stats}
                    enhancedMatches={enhancedMatches}
                    hydratedMatchGroups={hydratedMatchGroups}
                    organizedRooms={organizedRooms}
                    unmatchedLineItemsA={unmatchedLineItemsA}
                    unmatchedLineItemsB={unmatchedLineItemsB}
                    unmatchedInsurerRooms={unmatchedInsurerRooms}
                    unmatchedContractorRooms={unmatchedContractorRooms}
                    allRoomsList={allRoomsList}
                    insurerEstimateId={insurerEstimateId}
                    contractorEstimateId={contractorEstimateId}
                    filters={filters}
                    selectedItemIds={selection.selectedItemIds}
                    selectedRoomIds={selection.selectedRoomIds}
                    onApproveMatch={mutations.approveMatch}
                    onRejectMatch={mutations.rejectMatch}
                    onUpdateMatch={mutations.updateMatch}
                    onCreateMatch={mutations.createMatch}
                    onToggleRoomSelection={selection.toggleRoomSelection}
                    onClearRoomSelection={selection.clearSelection}
                    onToggleItemSelection={selection.toggleItemSelection}
                    onMoveItems={handleMoveItems}
                    onDeleteRoom={mutations.deleteRoom}
                    onCreateRoom={handleCreateRoomForOrganizeTab}
                    onMoveLineItem={handleMoveLineItem}
                    onSetItemsSelection={selection.setItemsSelection}
                    onTriggerCreateRoom={handleTriggerCreateRoom}
                    onNavigateToMatch={() => setActiveTab("matches")}
                    onRematchRoom={mutations.rematchRoom}
                    onApproveLineItemMatchGroup={mutations.approveLineItemMatchGroup}
                    onRejectLineItemMatchGroup={mutations.rejectLineItemMatchGroup}
                    onCreateLineItemMatchGroup={handleCreateLineItemMatchGroup}
                    onUpdateLineItemMatchGroup={handleUpdateLineItemMatchGroup}
                    onDeleteLineItem={mutations.deleteLineItem}
                    comparisonId={comparisonId}
                    canWrite={canWrite}
                    // Filter Props
                    overscopedIds={overscopedIds}
                    additionalIds={additionalIds}
                    discrepancyIds={discrepancyIds}
                    notOnInsuranceIds={notOnInsuranceIds}
                    toggleShowOverscoped={toggleShowOverscoped}
                    toggleShowAdditional={toggleShowAdditional}
                    toggleShowDiscrepancy={toggleShowDiscrepancy}
                    toggleShowNotOnInsurance={toggleShowNotOnInsurance}
                    applySingleFilter={applySingleFilter}
                    setActiveTab={setActiveTab}
                />
            </Tabs>

            <CreateRoomModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onCreate={handleCreateRoomSubmit}
                initialType={createModalType}
            />
        </div>
    );
}
